import React, { useState, useEffect } from 'react'
import { View, Button, RadioInput, Modal } from '@/components'
import { Channel } from 'stream-chat'
import { Chat, textVariant, theme } from '@/config'
import { createStyles } from '@codeleap/styles'
import { PropsOf } from '@codeleap/types'

type MentionsNotificationsModalProps = PropsOf<typeof Modal> & {
  channel: Channel
}

const mentionOptions = [
  { label: 'All messages', value: 'all' },
  { label: 'Mentions only', value: 'mentions' },
]

const getMentionPreference = (channel: Channel, userId: string) => {
  const cid = channel?.cid
  const storageKey = cid && userId ? `mention_notifications:${cid}:${userId}` : ''
  const stored = typeof window !== 'undefined' && storageKey ? window.localStorage.getItem(storageKey) : null
  const channelKey = userId ? `mention_notifications_${userId}` : ''

  return stored || (channel?.data as any)?.[channelKey] || 'all'
}

const setMentionPreference = async (channel: Channel, userId: string, preference: string) => {
  const cid = channel?.cid
  const storageKey = cid && userId ? `mention_notifications:${cid}:${userId}` : ''
  const channelKey = userId ? `mention_notifications_${userId}` : ''

  if (typeof window !== 'undefined' && storageKey) {
    window.localStorage.setItem(storageKey, preference)
  }

  if (channel && channelKey) {
    try {
      if (preference === 'all') {
        await channel.unmute()
        await Chat.Services.updatePushPreferences({
          chatId: channel?.id,
          chatLevel: 'all',
        })
      } else {
        await Chat.Services.updatePushPreferences({
          chatId: channel?.id,
          chatLevel: 'mentions',
        })
      }
    } catch (err) {
      console.warn('Failed to persist mention preference to channel', err)
    }
  }
}

export const MentionsNotifications = (props: MentionsNotificationsModalProps) => {
  const { channel, ...modalProps } = props
  const currentUserId = Chat.Store.userState.get()?.id
  const [selectedValue, setSelectedValue] = useState<string>('all')

  useEffect(() => {
    if (channel && currentUserId) {
      const preference = getMentionPreference(channel, currentUserId)
      setSelectedValue(preference)
    }
  }, [channel, currentUserId])

  const handleChange = async () => {
    if (!selectedValue || !currentUserId) return

    await setMentionPreference(channel, currentUserId, selectedValue)
    props?.toggle?.()
  }

  return (
    <Modal
      title='Mentions notifications'
      style={['center', 'box:noPadding', {
        titleWrapper: styles.titleWrapper,
        box: styles.modalBox,
      }]}
      showClose
      {...modalProps}
    >
      <View
        style={styles.content}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <RadioInput
          debugName='MentionsNotificationsModal:Radio'
          label=''
          value={selectedValue}
          onValueChange={(value) => {
            setSelectedValue(String(value))
          }}
          options={mentionOptions}
          style={['options:vertical', {
            optionWrapper: styles.radioOption,
            optionLabel: styles.radioLabel,
            optionIndicator: styles.radioIndicator,
            'optionIndicator:selected': styles.radioIndicatorSelected,
            optionIndicatorInner: styles.radioIndicatorInner,
          }]}
        />
        <View style={['row', 'gap:2', 'fullWidth', { marginTop: theme.spacing.value(.8) }]}>
          <Button
            debugName='MentionsNotificationsModal:Cancel'
            text='Cancel'
            onPress={props?.toggle}
            style={['small', 'neutralSecondary', 'flex', { wrapper: styles.buttonWrapper }]}
          />
          <Button
            debugName='MentionsNotificationsModal:Save'
            text='Save'
            onPress={handleChange}
            style={['small', 'flex', 'default', { wrapper: styles.buttonWrapper }]}
          />
        </View>
      </View>
    </Modal>
  )
}

const styles = createStyles((theme) => ({
  modalBox: {
    maxWidth: 453,
    width: '100%',
  },
  titleWrapper: {
    ...theme.spacing.padding(2),
  },
  content: {
    ...theme.presets.column,
    ...theme.spacing.paddingHorizontal(2),
    ...theme.spacing.paddingBottom(2),
    width: '100%',
  },
  buttonWrapper: {
    borderRadius: theme.radius.regular,
    height: theme.size.largeish,
    minHeight: theme.size.largeish,
  },
  radioOption: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    ...theme.presets.justifySpaceBetween,
    width: '100%',
    cursor: 'pointer',
  },
  radioLabel: {
    ...theme.spacing.marginLeft(0),
    ...theme.spacing.marginRight(0),
    flex: 1,
    order: 1,
    ...textVariant('p3'),
    color: theme.colors.textHeadingHighlighted,
    fontWeight: 500,
  },
  radioIndicator: {
    order: 2,
    ...theme.spacing.marginLeft(2),
    width: 20,
    height: 20,
  },
  radioIndicatorSelected: {
    backgroundColor: theme.baseColors.primarySolid500,
    ...theme.border({
      color: theme.baseColors.primarySolid500,
      width: 1,
    }),
  },
  radioIndicatorInner: {
    height: theme.size.small,
    width: theme.size.small,
  },
}))
